import {useLocation} from "react-router-dom-v5-compat";

import {
    useUserEnteredAnswerToQuestionListener,
    useVideoEndedListener,
} from "@khan/events-content";
import {useKhanmigoHasAssignmentRecommendationsListener} from "@khan/events-guide";
import {useCustomHomepageGreetingListener} from "@khan/events-homepage";
import {isLearnerPersona} from "@khan/guide-core-helpers";
import {isNABFeatureEnabled} from "@khan/guide-notifications";
import {GuideStateApi} from "@khan/guide-state";
import {useUserAttributes} from "@khan/shared-core";

import {useRequestNotificationDispatch} from "./request-notification-dispatch.ts";

/**
 * Subscribes to client events that may trigger a notification.
 *
 * When we encounter a client event that should result in a notification, we
 * send a request to the backend to build a notification that will eventually
 * be dispatched by the NotificationStreamListener for rendering.
 */
export const ClientEventListener = (): React.ReactNode => {
    const {pathname} = useLocation();
    const userAttributes = useUserAttributes();
    const requestNotificationDispatch = useRequestNotificationDispatch();

    // When Khanmigo has assignment recommendations, we want to ask the user if
    // they would like to see them.
    useKhanmigoHasAssignmentRecommendationsListener((event) => {
        // Don't request for this notification if KM is offline.
        if (GuideStateApi.latest().isOffline) {
            return;
        }
        requestNotificationDispatch({
            eventTrigger: "khanmigo-has-assignment-recommendations",
            dismissKey: event.dismissKey,
        });
    });

    // When a video ends, we want to ask the user if they would like to be
    // quizzed on the content.
    useVideoEndedListener((event) => {
        // Don't request for this notification if KM is offline.
        if (GuideStateApi.latest().isOffline) {
            return;
        }
        requestNotificationDispatch({
            eventTrigger: "video-ended",
            videoTitle: event.title,
        });
    });

    // When the user lands on the homepage, we want to greet the user with an engaging
    // message.
    useCustomHomepageGreetingListener((event) => {
        // Don't request for this notification if KM is offline.
        if (GuideStateApi.latest().isOffline) {
            return;
        }

        requestNotificationDispatch({
            eventTrigger: "custom-homepage-greeting",
            greetingType: event.greetingType,
        });
    });

    useUserEnteredAnswerToQuestionListener((event) => {
        // Don't request for this notification if KM is offline.
        if (GuideStateApi.latest().isOffline) {
            return;
        }

        // userEnteredAnswerToQuestion events are being generated for all task
        // types, but we only want to show the notification for practice tasks,
        // since they are the only task types where Khanmigo is enabled.
        if (event.taskType !== "practice") {
            return;
        }

        // Don't request for this notification if the user is not a learner
        // or simulating a learner.
        if (
            !isLearnerPersona({
                userAttributes,
                pathname,
                isEmulatingLearner: GuideStateApi.latest().userAsStudent,
            })
        ) {
            return;
        }

        if (!isNABFeatureEnabled(userAttributes)) {
            return;
        }

        requestNotificationDispatch({
            eventTrigger: "user-entered-answer-to-question",
        });
    });

    return null;
};
